import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Clock, User, Tag } from "lucide-react";
import { Button } from "./ui/button";
import { BLOG_ARTICLES } from "../data/blogData";

/* ── Blog Listing ── */
export const BlogListPage = () => {
  return (
    <>
      <section className="bg-[#2C2A2B] pt-32 pb-16 lg:pt-40 lg:pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#E5BD77] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
            <div className="inline-flex items-center gap-2 bg-[#308795]/20 text-[#80d4df] text-sm font-semibold px-4 py-1.5 rounded-full">
              <span className="w-2 h-2 bg-[#308795] rounded-full" />
              Insights & Articles
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-3">Blog</h1>
          <p className="text-lg text-[#A9B294]">Insights, thought leadership, and practical guidance for SME digital transformation.</p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[#FAFAF7]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-6">
            {BLOG_ARTICLES.map((article) => (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                className="block group"
                onClick={() => window.scrollTo(0, 0)}
              >
                <div className="bg-white border border-[#E0D1BB]/30 rounded-2xl p-7 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-bold text-[#E95D54] bg-[#E95D54]/[0.08] px-3 py-1 rounded-full">{article.category}</span>
                    <span className="text-xs text-[#8A8385] flex items-center gap-1"><Clock className="w-3 h-3" />{article.readTime}</span>
                    <span className="text-xs text-[#8A8385]">{article.date}</span>
                  </div>
                  <h2 className="text-xl lg:text-2xl font-bold text-[#2C2A2B] leading-snug mb-3 group-hover:text-[#308795] transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-sm text-[#6B6567] leading-relaxed mb-4">{article.excerpt}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#308795] group-hover:gap-3 transition-all">
                    Read article <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

/* ── Single Blog Article ── */
export const BlogArticlePage = () => {
  const { slug } = useParams();
  const article = BLOG_ARTICLES.find((a) => a.slug === slug);

  if (!article) return (
    <section className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-[#2C2A2B] mb-4">Article not found</h1>
        <Link to="/blog" className="text-[#308795] font-semibold hover:underline">Back to Blog</Link>
      </div>
    </section>
  );

  return (
    <>
      {/* Hero */}
      <section className="bg-[#2C2A2B] pt-32 pb-16 lg:pt-40 lg:pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#E5BD77] transition-colors" onClick={() => window.scrollTo(0, 0)}>
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
            <span className="text-xs font-bold text-[#E95D54] bg-[#E95D54]/15 px-3 py-1 rounded-full">{article.category}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold text-white leading-tight mb-5">{article.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-[#A9B294]">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4" />{article.author}</span>
            <span>{article.date}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{article.readTime}</span>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      {article.heroImage && (
        <div className="bg-[#2C2A2B] pb-8">
          <div className="max-w-4xl mx-auto px-6">
            <img src={article.heroImage} alt={article.title} className="w-full rounded-2xl shadow-xl" />
          </div>
        </div>
      )}

      {/* Content */}
      <section className="py-16 lg:py-24 bg-[#FAFAF7]">
        <div className="max-w-3xl mx-auto px-6">
          {article.sections.map((sec, i) => {
            switch (sec.type) {
              case "intro":
                return sec.content.split("\n\n").map((p, j) => (
                  <p key={`intro-${j}`} className="text-base text-[#494546] leading-relaxed mb-5 first:text-lg first:text-[#2C2A2B]">{p}</p>
                ));
              case "heading":
                return <h2 key={i} className="text-2xl font-bold text-[#2C2A2B] mt-10 mb-4 pb-2 border-b border-[#E0D1BB]/40">{sec.content}</h2>;
              case "subheading":
                return <h3 key={i} className="text-lg font-bold text-[#308795] mt-6 mb-3">{sec.content}</h3>;
              case "paragraph":
                return sec.content.split("\n\n").map((p, j) => (
                  <p key={`p-${i}-${j}`} className="text-sm text-[#6B6567] leading-relaxed mb-4">{p}</p>
                ));
              case "bullets":
                return (
                  <ul key={i} className="space-y-2 mb-5 pl-1">
                    {sec.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#308795] mt-2 shrink-0" />
                        <span className="text-sm text-[#494546]">{item}</span>
                      </li>
                    ))}
                  </ul>
                );
              case "checks":
                return (
                  <div key={i} className="space-y-2 mb-5">
                    {sec.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-[#7EA13E] mt-0.5 shrink-0" />
                        <span className="text-sm text-[#494546]">{item}</span>
                      </div>
                    ))}
                  </div>
                );
              case "table":
                return (
                  <div key={i} className="mb-6 overflow-hidden rounded-xl border border-[#E0D1BB]/30">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#308795]/[0.06]">
                          {sec.headers.map((h, j) => (
                            <th key={j} className="text-left text-xs font-bold text-[#308795] uppercase tracking-wider px-5 py-3">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sec.rows.map((row, j) => (
                          <tr key={j} className="border-t border-[#E0D1BB]/20">
                            {row.map((cell, k) => (
                              <td key={k} className="text-sm text-[#494546] px-5 py-3 font-medium">{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              default:
                return null;
            }
          })}

          {/* Learn More */}
          <div className="mt-12 bg-[#465155] rounded-2xl p-8 lg:p-10">
            <h3 className="text-xl font-bold text-white mb-5">Learn More</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-[#E5BD77] mb-1">Asterisk Computer</p>
                <a href="https://asterisk.com.sg" target="_blank" rel="noopener noreferrer" className="text-sm text-[#80d4df] hover:text-white transition-colors underline">https://asterisk.com.sg</a>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#E5BD77] mb-1">Apper Cloud Labs</p>
                <a href="https://apper.cloud/" target="_blank" rel="noopener noreferrer" className="text-sm text-[#80d4df] hover:text-white transition-colors underline">https://apper.cloud/</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
